import { GameBackground, Heading } from '@repo/ui'
import { observer } from 'mobx-react-lite'
import Balancer from 'react-wrap-balancer'

import { BrandImage } from '@/components/BrandImage'
import { DisplayGameCode } from '@/components/DisplayGameCode'
import { TriviaList } from '@/components/TriviaList'
import { JumbotronVideoTrivia } from '@/components/video/JumbotronVideoTrivia'
import { DisplayQrCode } from '@/pages/game/jumbotron/components/DisplayQrCode'

import { useGameContext } from '../../context/game-context'
import { VideoContainerStyled } from '../VideoOverlay'
import { CurrentColumnName } from './CurrentColumnName'

type JumbotronTriviaProps = {
  enableBrandImage: boolean
  enableGameCode: boolean
  enableQRCode: boolean
  enableQRCodeFullscreen: boolean
  showCurrentLetter: boolean
  showGameInfo: boolean
}

export const JumbotronTrivia = observer(
  ({
    enableBrandImage,
    enableGameCode,
    enableQRCode,
    enableQRCodeFullscreen,
    showCurrentLetter,
    showGameInfo
  }: JumbotronTriviaProps) => {
    return (
      <TriviaBackground>
        {showGameInfo && (
          <div className='fixed top-0 flex w-full items-center justify-between gap-8 overflow-hidden p-8'>
            <div className='w-32'>{enableBrandImage && <BrandImage />}</div>

            {enableGameCode && (
              <div className='mx-auto flex items-center justify-center sm:ml-auto lg:mr-0'>
                <DisplayGameCode />
              </div>
            )}

            {enableQRCode && (
              <div>
                <DisplayQrCode className='m-auto rounded-xl' />
              </div>
            )}
          </div>
        )}

        {enableQRCode && enableQRCodeFullscreen ? (
          <DisplayQrCode isFullscreen />
        ) : (
          <TriviaContent showCurrentLetter={showCurrentLetter} />
        )}
      </TriviaBackground>
    )
  }
)

const TriviaBackground = observer(({ children }: { children: React.ReactNode }) => {
  return (
    <GameBackground>
      <VideoContainerStyled>
        <JumbotronVideoTrivia />
        <div className='fixed inset-0 h-screen w-screen bg-[rgba(0,0,0,0.5)] mix-blend-darken' />

        {children}
      </VideoContainerStyled>
    </GameBackground>
  )
})

type TriviaContentProps = {
  showCurrentLetter: boolean
}

const TriviaContent = observer(({ showCurrentLetter }: TriviaContentProps) => {
  const { currentItemIndex, gameData, isStartedGame } = useGameContext()

  if (gameData.type !== 'trivia' || currentItemIndex === null) return null

  const currentDrawnItem = gameData.drawnItems[currentItemIndex]

  const { enablePreviousAnswers, enablePreviousQuestions } = gameData.jumbotron

  const showPrevious = (enablePreviousQuestions || enablePreviousAnswers) && currentItemIndex > 0

  return (
    <div className='relative flex w-full flex-1 items-center justify-center text-center'>
      {!isStartedGame && (
        <div className='flex w-full flex-col items-center justify-center gap-6 text-center'>
          <Balancer>
            <Heading size='hero'>Welcome!</Heading>
          </Balancer>
          <Balancer>
            <Heading size='large' disableTruncation>
              The host will start the game in a moment
            </Heading>
          </Balancer>
        </div>
      )}

      {isStartedGame && (
        <div className='flex w-full flex-col gap-8'>
          {showCurrentLetter && <CurrentColumnName />}

          <div className='flex min-h-72 flex-col items-center justify-center text-center'>
            <Balancer>
              <Heading size='hero'>{currentDrawnItem?.trivia.question}</Heading>
            </Balancer>
          </div>

          {showPrevious && (
            <div className='ml-8 mr-auto flex flex-col items-center justify-center rounded-lg bg-[rgba(0,0,0,0.66)] px-4 py-8 text-left'>
              <h2 className='text-lg font-bold'>
                Previous{' '}
                {enablePreviousAnswers && !enablePreviousQuestions ? 'Answers' : 'Questions'}
              </h2>
              <TriviaList
                enableShowAnswerButton={false}
                scrollContainer='element'
                scrollToItem='previous'
                showAnswer={enablePreviousAnswers}
                showFullList={false}
                showQuestion={enablePreviousQuestions}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
})
